/**
 * PROTECTED FILE — DO NOT MODIFY WITHOUT PERMISSION
 *
 * Last verified working: 2026-01-03
 *
 * This file controls: page routing and navigation
 * Changes here have historically broken core game function.
 *
 * Before modifying:
 * 1. Get explicit approval from Zach
 * 2. Create a checkpoint commit
 * 3. Test full baseline after ANY change
 */

// Routes.jsx - Route management page
// Simple mode route management (Simulation mode archived for post-launch)

import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useWorldState } from "../components/state/WorldStateContext.jsx";
import RoutesPanel from "../components/simple/RoutesPanel.jsx";
// SlotSchedulePanel archived - simulation mode only
import NewRouteModal from "../components/simple/NewRouteModal.jsx";
import ScheduleFlightModal from "../components/simple/ScheduleFlightModal.jsx";
import RevenueTicker from "../components/ui/RevenueTicker.jsx";
import NewsTicker from "../components/ui/NewsTicker.jsx";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { debug } from '../lib/debug.js';
// Icons removed - text-only UI
// SimulationRoutesPanel archived - see src/archive/simulation/

export default function RoutesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { worldState } = useWorldState();
  const [showNewRouteModal, setShowNewRouteModal] = useState(false);
  const [scheduleRoute, setScheduleRoute] = useState(null);
  // Simulation mode archived - slots initialization removed

  // Handle tracking a flight on the map
  const handleTrackFlight = useCallback((flight) => {
    navigate("/worldmap", { state: { trackFlightId: flight.id } });
  }, [navigate]);

  // Handle new route creation
  const handleNewRoute = useCallback(() => {
    setShowNewRouteModal(true);
  }, []);

  const handleRouteCreated = useCallback((route) => {
    debug("[Routes] Route created:", route);
  }, []);

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: "#000" }}>
      {/* Header */}
      <div
        className="sticky top-0 z-40 flex items-center justify-between p-4 flex-shrink-0"
        style={{ backgroundColor: "#0a0a0a", borderBottom: "1px solid #1a1a1a" }}
      >
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/opscenter")}
            style={{ color: "#a1a1a1" }}
          >
            {t('common.back')}
          </Button>
          <h1 className="text-xl font-bold" style={{ color: "#fff" }}>
            {t('nav.routes')}
          </h1>
        </div>

        <div className="flex items-center gap-4">
          {/* Game Clock - same retro LED style as WorldMap */}
          {worldState?.gameTime && (() => {
            const gt = new Date(worldState.gameTime);
            const h = gt.getHours();
            const m = gt.getMinutes();
            const dh = h % 12 || 12;
            const ap = h >= 12 ? 'PM' : 'AM';
            return (
              <div className="px-3 py-1.5 rounded" style={{
                backgroundColor: "#0a0a0a",
                border: "2px solid #1a1a1a",
                boxShadow: "inset 0 2px 8px rgba(0,0,0,0.8)",
              }}>
                <div style={{
                  fontFamily: "'Segment7', 'DSEG7 Classic', 'Courier New', monospace",
                  fontSize: "1.1rem",
                  fontWeight: "bold",
                  color: "#ff2020",
                  textShadow: "0 0 10px #ff2020, 0 0 20px #ff2020",
                  letterSpacing: "2px",
                  lineHeight: 1,
                }}>
                  {`${dh.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')} ${ap}`}
                </div>
                <div className="text-center" style={{ color: "#ff2020", opacity: 0.7, fontSize: "9px", marginTop: "2px" }}>
                  DAY {worldState.dayNumber || 1}
                </div>
              </div>
            );
          })()}

          {/* Revenue Ticker */}
          <RevenueTicker position="header" />

          {/* Quick stats */}
          <div className="text-sm" style={{ color: "#737373" }}>
            <span style={{ color: "#22c55e" }}>{worldState?.routes?.length || 0}</span> {t('common.routes')}
            <span className="mx-2">|</span>
            <span style={{ color: "#3b82f6" }}>{worldState?.aircraft?.length || 0}</span> {t('common.aircraft')}
          </div>

          {/* Map button */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate("/worldmap")}
            style={{ borderColor: "#525252", color: "#a1a1a1" }}
          >
            {t('ops.viewMap')}
          </Button>
        </div>
      </div>

      {/* Main content - full width for grid layout */}
      <div className="flex-1 px-4 py-4">
        {/* Simple mode only - Simulation mode archived for post-launch */}
        <RoutesPanel
          onNewRoute={handleNewRoute}
          onTrackFlight={handleTrackFlight}
          onSchedule={(route) => setScheduleRoute(route)}
        />
      </div>

      {/* News Ticker */}
      <NewsTicker worldState={worldState} />

      {/* New Route Modal */}
      <NewRouteModal
        isOpen={showNewRouteModal}
        onClose={() => setShowNewRouteModal(false)}
        onCreateRoute={handleRouteCreated}
      />

      {/* Schedule Flight Modal */}
      <ScheduleFlightModal
        isOpen={!!scheduleRoute}
        onClose={() => setScheduleRoute(null)}
        preselectedRoute={scheduleRoute}
      />
    </div>
  );
}
